import { useEffect, useMemo, useState } from 'react'
import { Button, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { useActiveOrganization, useFetchActiveOrganization } from 'hooks/use-organization'
import { authenticatedUserAtom, toastAtom } from 'state'
import { SetterOrUpdater, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import FloatingButtonBar from 'components/FloatingButtonBar'
import EditablePicture from 'components/EditablePicture'
import { OnboardingStep, Twin, TwinUpdate } from 'api/model'
import {
  twinsDeletePicture,
  twinsOrderInstructions,
  twinsUpdate,
  twinsUploadPicture,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { twinsAtom } from 'state/twins'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import withOnboardingStep, { UpdateOnboardingStepOption } from 'components/FTUE/withOnboardingStep'
import SuggestedQuestionItem from './SuggestedQuestion/SuggestedQuestionItem'
import SuggestedQuestionModal from './SuggestedQuestion/SuggestedQuestionModal'
import { useFetchInstructions, useFetchQuestionTypeInstructions } from 'hooks/use-instructions'
import { UsernameInput } from 'components/FTUE/Onboarding'
import { myTwinAtom } from 'state/my-twin'
import ReactDragListView from 'react-drag-listview'
import { isFieldEmpty } from 'utils'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import FtuxTooltip from 'components/FtuxTooltip'
import { useNavigate } from 'react-router-dom'
import { useFtuxTooltip } from 'hooks/use-ftux'
import TwinFtuxModal from 'components/FtuxModal/TwinFtuxModal'
import { COLORS } from 'app-constants'

const MyTwinProfile = ({
  updateOnboardingStep,
}: {
  updateOnboardingStep: (step?: UpdateOnboardingStepOption) => void
}) => {
  const isMobile = useCheckMobileScreen()

  const user = useRecoilValue(authenticatedUserAtom)
  const { isLoading: isLoadingActiveOrganization, organization, error } = useFetchActiveOrganization()

  const [twins, setTwins] = useRecoilState(twinsAtom)
  const setMyTwin = useSetRecoilState(myTwinAtom)
  const twin = useMemo(() => {
    return twins.find((t) => t.user.email === user?.email)
  }, [twins, user?.email])

  useFetchInstructions(twin?.id ?? '')

  const setToast = useSetRecoilState(toastAtom)

  const onPicturePicked: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!organization || !twin || !event.target.files) {
      return
    }

    try {
      const file = event.target.files[0]
      const result = await twinsUploadPicture(organization.id, twin.id, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      })

      // update twin picture in recoil
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === twin.id) {
            return Object.assign({}, t, { picture: result.data.picture })
          }
          return t
        })
      })
    } catch (Error) {
      setToast({ title: 'Failed to upload twin photo', severity: 'error' })
    }
  }

  const onPictureRemoved = async () => {
    if (!organization || !twin) {
      return
    }

    try {
      await twinsDeletePicture(organization.id, twin.id)
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === twin.id) {
            return Object.assign({}, t, { picture: null })
          }
          return t
        })
      })
    } catch (error) {
      setToast({ title: 'Twin photo removal failed', severity: 'error' })
    }
  }

  const renderContent = () => {
    // TODO: add loading state
    if (isLoadingActiveOrganization) {
      return null
    }

    if (error) {
      return <div>{error}</div>
    }

    if (!twin) {
      return null
    }

    const renderLeftColumn = () => {
      if (!organization) {
        return <LoadingTwinProfile />
      }

      return (
        <Stack width={isMobile ? '100%' : '376px'}>
          <EditProfile
            setMyTwin={setMyTwin}
            twin={twin}
            onFinishedEditing={(didUpdate) => {
              if (didUpdate) {
                setToast({ title: 'Twin profile updated successfully', severity: 'success' })
              }
            }}
            updateOnboardingStep={updateOnboardingStep}
          />
        </Stack>
      )
    }

    const renderPicture = () => {
      // if there is no picture yet for the twin, we will default it to the user's profile picture instead
      if (!organization) {
        return <Skeleton variant="rectangular" height={160} width={160} sx={{ marginTop: '8px' }} />
      }

      return (
        <EditablePicture
          onPicturePicked={onPicturePicked}
          onPictureRemoved={onPictureRemoved}
          src={twin?.picture}
          changeLabel="logo"
          alt="Twin profile"
          style={{ borderRadius: '9999px', height: '160px', width: '160px' }}
        />
      )
    }

    return (
      <Stack spacing={6}>
        {renderPicture()}
        {renderLeftColumn()}
      </Stack>
    )
  }

  return (
    <Stack
      p={3}
      sx={{
        borderLeft: `1px solid ${COLORS.GRAY_5}`,
        backgroundColor: COLORS.GRAY_8,
      }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}>
        {!isMobile && (
          <Typography
            variant="h4"
            sx={{
              marginBottom: '24px',
            }}>
            Description
          </Typography>
        )}
      </Stack>

      {renderContent()}
    </Stack>
  )
}

const SuggestedQuestionsList = ({ twinId }: { twinId: string }) => {
  const isMobile = useCheckMobileScreen()
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const { questionTypeInstructions, setQuestionTypeInstructions } = useFetchQuestionTypeInstructions(twinId)

  const [isAddOrEditSuggestedQuestionModalOpen, setIsAddOrEditSuggestedQuestionModalOpen] = useState(false)
  const [currentSuggestedQuestionId, setCurrentSuggestedQuestionId] = useState<string>('')

  const currentSuggestedQuestion = questionTypeInstructions.find(
    (suggestedQuestion) => suggestedQuestion.id === currentSuggestedQuestionId
  )

  const onSuggestedQuestionClick = (suggestedQuestionId: string) => {
    setCurrentSuggestedQuestionId(suggestedQuestionId)
    setIsAddOrEditSuggestedQuestionModalOpen(true)
  }

  const onAddSuggestionClick = () => {
    setCurrentSuggestedQuestionId('')
    setIsAddOrEditSuggestedQuestionModalOpen(true)
  }

  const renderSuggestedQuestions = () => {
    // we should show a maximum of MAX_SUGGESTED_QUESTIONS suggested questions, we try to use what's available from the backend first and then fill the rest with nulls
    const dragProps = {
      async onDragEnd(fromIndex: number, toIndex: number) {
        const data = [...questionTypeInstructions]
        const item = data.splice(fromIndex, 1)[0]
        data.splice(toIndex, 0, item)

        if (!activeOrganization) {
          return
        }

        try {
          const instructionIds = data.map((instruction) => instruction.id)
          const response = await twinsOrderInstructions(activeOrganization, twinId, instructionIds)
          setQuestionTypeInstructions(response.data)
          setToast({ title: 'Suggested questions updated successfully', severity: 'success' })
        } catch {
          setToast({ title: 'Suggested questions update failed', severity: 'error' })
        }
      },
      nodeSelector: 'div',
      handleSelector: 'svg',
    }

    return (
      <Stack mt={-2} mb={isMobile ? 4 : 0}>
        <Stack>
          <Typography
            variant="h7.5"
            sx={{
              mb: '4px',
            }}>
            Conversation starters
          </Typography>

          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_3,
            }}>
            Add up to 10 conversation starters. The top 5 will be shown, drag and drop to switch the order.
          </Typography>
        </Stack>

        <Button
          onClick={onAddSuggestionClick}
          startIcon={
            <AddOutlinedIcon
              sx={{
                width: '16px',
                height: '16px',
                color: COLORS.GRAY_2,
              }}
            />
          }
          sx={{
            width: 'fit-content',
            color: COLORS.GRAY_2,
            my: 2,
          }}>
          Add conversation starter
        </Button>

        <ReactDragListView {...dragProps}>
          {questionTypeInstructions.map((suggestedQuestion, index) => {
            return (
              <SuggestedQuestionItem
                key={index}
                suggestedQuestion={suggestedQuestion}
                onSuggestedQuestionClick={onSuggestedQuestionClick}
              />
            )
          })}
        </ReactDragListView>
      </Stack>
    )
  }

  return (
    <Stack>
      {renderSuggestedQuestions()}
      {isAddOrEditSuggestedQuestionModalOpen && (
        <SuggestedQuestionModal
          suggestedQuestion={currentSuggestedQuestion}
          onClose={() => setIsAddOrEditSuggestedQuestionModalOpen(false)}
        />
      )}
    </Stack>
  )
}

// this is used to get the unique id of the short url which normally looks like '/s/123456'
function getSubstringAfterS(inputString: string) {
  var parts = inputString.split('/s/')
  if (parts.length > 1) {
    return parts[1] // Return the part after '/s/'
  } else {
    return '' // Return an empty string if '/s/' is not found
  }
}

export function EditProfile({
  onFinishedEditing,
  twin,
  setMyTwin,
  updateOnboardingStep,
}: {
  onFinishedEditing: (didUpdate: boolean) => void
  twin: Twin
  setMyTwin: SetterOrUpdater<Twin | null>
  updateOnboardingStep: (steps?: UpdateOnboardingStepOption) => void
}) {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()

  const setToast = useSetRecoilState(toastAtom)

  const setTwins = useSetRecoilState(twinsAtom)

  const [name, setName] = useState<string>(twin.name)
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChangesMade(true)
    setName(event.target.value)
  }

  const [publicSummary, setPublicSummary] = useState<string>(twin.public_summary ?? '')
  const onPublicSummaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChangesMade(true)
    setPublicSummary(event.target.value)
  }

  const [title, setTitle] = useState<string>(twin.title ?? '')
  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChangesMade(true)
    setTitle(event.target.value)
  }

  const [shortUrl, setShortUrl] = useState<string>(getSubstringAfterS(twin.twin_share?.link ?? ''))
  const onShortUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShortUrl(event.target.value)
    setIsChangesMade(true)
  }
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null)
  useEffect(() => {
    if (isUsernameAvailable) {
      setIsChangesMade(true)
    }
  }, [isUsernameAvailable])

  const [characterSummary, setCharacterSummary] = useState<string>(twin.character_summary ?? '')
  const onCharacterSummaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChangesMade(true)
    setCharacterSummary(event.target.value)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const onFormSubmit = async () => {
    setIsSubmitting(true)
    setSubmitCount(submitCount + 1)

    if (!activeOrganization || isFieldEmpty(name) || isFieldEmpty(title) || isFieldEmpty(publicSummary)) {
      setIsSubmitting(false)
      return null
    }

    try {
      const update: TwinUpdate = {
        name,
        title,
        public_summary: publicSummary,
        character_summary: characterSummary,
        short_url: shortUrl,
      }
      const result = await twinsUpdate(activeOrganization, twin.id, update)

      // update twins in recoil
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === twin.id) {
            return result.data
          }
          return t
        })
      })

      // update my twin atom in recoil
      setMyTwin((myTwin) => {
        if (!myTwin) {
          return null
        }
        return result.data
      })
      onFinishedEditing(true)
      setIsChangesMade(false)
    } catch (error) {
      onFinishedEditing(false)
      setToast({ title: 'Twin profile update failed', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCancelClicked = () => {
    onFinishedEditing(false)
    setIsChangesMade(false)
  }

  // add local state for whether or not any changes were made
  const [isChangesMade, setIsChangesMade] = useState(false)

  // local state for whether or not the ftux tooltip is open
  const user = useRecoilValue(authenticatedUserAtom)
  const hasShownMyTwinFtuxModal = !!user?.onboarding_steps_completed.includes(OnboardingStep.my_twin_modal_shown)
  const [isTwinFtuxModalOpen, setIsTwinFtuxModalOpen] = useState<boolean>(!hasShownMyTwinFtuxModal)
  const { isFtuxTooltipOpen, setIsFtuxTooltipOpen } = useFtuxTooltip()

  const onFtuxModalButtonClick = () => {
    setIsTwinFtuxModalOpen(false)
    setIsFtuxTooltipOpen(true)
    updateOnboardingStep({ onboardingStep: OnboardingStep.my_twin_modal_shown })
  }

  return (
    <>
      <Stack spacing={6} sx={{ width: isMobile ? '100%' : '581px' }}>
        <TextFieldWithDarkMode
          className="has-label"
          required={true}
          theme={theme}
          isDarkBackdrop={true}
          label="Name"
          placeholder="What is the name of your twin?"
          value={name}
          onChange={onNameChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(name)}
          helperText={submitCount > 0 && isFieldEmpty(name) && 'You must enter a name'}
        />

        <TextFieldWithDarkMode
          theme={theme}
          className="has-label"
          required={true}
          isDarkBackdrop={true}
          label="Headline"
          placeholder="e.g. Product Manager at Startup"
          value={title}
          onChange={onTitleChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(title)}
          helperText={submitCount > 0 && isFieldEmpty(title) && 'You must enter a headline'}
        />

        <TextFieldWithDarkMode
          theme={theme}
          required={true}
          isDarkBackdrop={true}
          className="has-label"
          multiline
          rows={3}
          label="About"
          placeholder="What should users know about your twin? What are your areas of expertise? What is your background?"
          value={publicSummary}
          onChange={onPublicSummaryChange}
          disabled={isSubmitting}
          error={submitCount > 0 && isFieldEmpty(publicSummary)}
          helperText={submitCount > 0 && isFieldEmpty(publicSummary) && 'You must enter an about summary'}
        />

        <Stack>
          <UsernameInput
            twinId={twin.id}
            username={shortUrl}
            onShortUrlChange={onShortUrlChange}
            isUsernameAvailable={isUsernameAvailable}
            setIsUsernameAvailable={setIsUsernameAvailable}
          />
        </Stack>

        <FtuxTooltip
          open={isFtuxTooltipOpen}
          setOpen={setIsFtuxTooltipOpen}
          headerText="Customize your twin"
          subtitleText="Instruct your twin on how it should respond and add conversation starters to encourage other users to chat with your twin."
          progressText="1 of 6"
          onNextClick={() => {
            navigate('/my-twin/notes')
          }}
          placement={isMobile ? 'bottom' : 'right'}
          onDismiss={() => {
            setIsFtuxTooltipOpen(false)
          }}>
          <TextFieldWithDarkMode
            theme={theme}
            isDarkBackdrop={true}
            multiline
            className="has-label"
            label="Instructions"
            placeholder="What does your twin do? How does it behave? Things to avoid doing?"
            value={characterSummary}
            onChange={onCharacterSummaryChange}
            disabled={isSubmitting}
          />
        </FtuxTooltip>

        <SuggestedQuestionsList twinId={twin.id} />
      </Stack>

      {isTwinFtuxModalOpen && (
        <TwinFtuxModal
          onButtonClick={onFtuxModalButtonClick}
          onClose={() => {
            setIsTwinFtuxModalOpen(false)
          }}
        />
      )}

      {isChangesMade && (
        <FloatingButtonBar
          onCancelClicked={onCancelClicked}
          updateLabel="Save changes"
          onUpdateClicked={onFormSubmit}
          isUpdating={isSubmitting}
        />
      )}
    </>
  )
}

const LoadingTwinProfile = () => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }}>
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
    </Stack>
  )
}

export default withOnboardingStep(MyTwinProfile, OnboardingStep.twin_share_url_generated)
